// Alta de una cuenta personal (docs/cuentas-personales.md). Se llega por el enlace de un solo uso que da Pau
// (/alta#<token>): el token va detrás de «#» para que no salga del navegador. Tres pasos en una pantalla:
// escanear el QR con Google Authenticator, elegir contraseña y confirmar con el primer código. Al terminar se
// enseñan los 10 códigos de recuperación UNA sola vez, y hasta que no se marca «los he guardado» no se entra.
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { Copy, Download } from 'lucide-react';
import { api, ApiHttpError } from '../lib/api';
import { Aviso, Boton, Campo, Casilla, Etiqueta } from '../features/oficina/ui';
import { t } from '../i18n';

interface Invitacion {
  nombre: string;
  usuario: string;
  otpauth: string;
  secreto: string;
}

const MIN = 10;

export function AltaPage() {
  const token = useMemo(() => window.location.hash.replace(/^#/, '').trim(), []);
  const [inv, setInv] = useState<Invitacion | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [errorEnlace, setErrorEnlace] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [codigos, setCodigos] = useState<string[] | null>(null);
  const [guardados, setGuardados] = useState(false);
  const [verSecreto, setVerSecreto] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorEnlace(t('cuentas.alta.enlaceIncompleto'));
      return;
    }
    api<Invitacion>('/api/auth/alta/ver', { method: 'POST', body: { token } })
      .then(async (r) => {
        setInv(r);
        setQr(await QRCode.toDataURL(r.otpauth, { margin: 1, width: 220 }));
      })
      .catch((e: unknown) => setErrorEnlace(e instanceof ApiHttpError ? e.message : t('cuentas.alta.errorEnlace')));
  }, [token]);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN) return setError(t('cuentas.alta.errMin', { min: MIN }));
    if (password !== password2) return setError(t('cuentas.alta.errNoCoinciden'));
    if (!/^\d{6}$/.test(codigo.replace(/\s/g, ''))) return setError(t('cuentas.alta.errCodigo'));
    setEnviando(true);
    try {
      const r = await api<{ ok: true; codigos: string[] }>('/api/auth/alta', {
        method: 'POST',
        body: { token, password, codigo: codigo.replace(/\s/g, '') },
      });
      // El enlace ya no vale: que no se quede en el historial del navegador.
      window.history.replaceState(null, '', '/alta');
      setPassword('');
      setPassword2('');
      setCodigos(r.codigos);
    } catch (err) {
      setError(err instanceof ApiHttpError ? err.message : t('cuentas.alta.errGenerico'));
      setCodigo('');
    } finally {
      setEnviando(false);
    }
  }

  const textoCodigos = codigos ? `${t('cuentas.alta.fichero', { nombre: inv?.nombre ?? '' })}\n\n${codigos.join('\n')}\n` : '';

  return (
    <div className="flex min-h-full items-start justify-center bg-oficina-fondo px-4 py-10 text-oficina-texto" data-testid="alta-page">
      <div className="w-full max-w-md space-y-5 rounded-o-m bg-oficina-panel p-6 ring-1 ring-white/10">
        {errorEnlace ? (
          <>
            <h1 className="text-o-titulo2 font-semibold">{t('cuentas.alta.tituloEnlace')}</h1>
            <Aviso tono="error">{errorEnlace}</Aviso>
          </>
        ) : !inv ? (
          <p className="text-oficina-texto2">{t('cuentas.alta.abriendo')}</p>
        ) : codigos ? (
          <>
            <h1 className="text-o-titulo2 font-semibold">{t('cuentas.alta.listo', { nombre: inv.nombre })}</h1>
            <p className="text-oficina-texto2">{t('cuentas.alta.codigosExplica')}</p>
            <p className="font-semibold">{t('cuentas.alta.soloAhora')}</p>
            <ol className="grid grid-cols-2 gap-2 rounded-o-s bg-black/30 p-4 font-mono text-o-cuerpo" data-testid="alta-codigos">
              {codigos.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ol>
            <div className="flex flex-wrap gap-2">
              <Boton icono={Copy} onClick={() => void navigator.clipboard?.writeText(textoCodigos)}>
                {t('cuentas.alta.copiar')}
              </Boton>
              <Boton
                icono={Download}
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(new Blob([textoCodigos], { type: 'text/plain' }));
                  a.download = 'codigos-recuperacion-oficina.txt';
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                {t('cuentas.alta.descargar')}
              </Boton>
            </div>
            <Casilla marcada={guardados} alCambiar={setGuardados}>
              {t('cuentas.alta.guardados')}
            </Casilla>
            <Boton
              variante="primario"
              className="w-full"
              disabled={!guardados}
              // La sesión ya está abierta (cookie): cargar de nuevo hace que toda la app la vea.
              onClick={() => window.location.assign('/')}
            >
              {t('cuentas.alta.entrar')}
            </Boton>
          </>
        ) : (
          <form onSubmit={enviar} noValidate className="space-y-5">
            <h1 className="text-o-titulo2 font-semibold">{t('cuentas.alta.hola', { nombre: inv.nombre })}</h1>
            <p className="text-oficina-texto2">{t('cuentas.alta.explica', { usuario: inv.usuario })}</p>

            <div className="space-y-2">
              <p className="font-semibold">{t('cuentas.alta.paso1')}</p>
              <p className="text-o-texto text-oficina-texto2">{t('cuentas.alta.paso1Ayuda')}</p>
              {qr ? <img src={qr} alt={t('cuentas.alta.qrAlt')} className="mx-auto rounded-o-s bg-white p-2" width={220} height={220} /> : null}
              <button type="button" className="text-o-texto text-oficina-enlace underline underline-offset-2" onClick={() => setVerSecreto((v) => !v)}>
                {verSecreto ? t('cuentas.alta.ocultarClave') : t('cuentas.alta.verClave')}
              </button>
              {verSecreto ? (
                <p className="break-all rounded-o-s bg-black/30 p-3 font-mono text-o-texto" data-testid="alta-secreto">
                  {inv.secreto.replace(/(.{4})/g, '$1 ').trim()}
                </p>
              ) : null}
            </div>

            <div className="space-y-3">
              <p className="font-semibold">{t('cuentas.alta.paso2')}</p>
              <Etiqueta titulo={t('cuentas.alta.contrasena')} ayuda={t('cuentas.alta.contrasenaAyuda', { min: MIN })}>
                <Campo type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} maxLength={72} />
              </Etiqueta>
              <Etiqueta titulo={t('cuentas.alta.repite')}>
                <Campo type="password" autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} maxLength={72} />
              </Etiqueta>
            </div>

            <div className="space-y-3">
              <p className="font-semibold">{t('cuentas.alta.paso3')}</p>
              <Campo
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                maxLength={7}
                className="tracking-widest"
                aria-label={t('cuentas.alta.codigoAria')}
              />
            </div>

            {error ? <Aviso tono="error">{error}</Aviso> : null}
            <Boton type="submit" variante="primario" className="w-full" cargando={enviando}>
              {t('cuentas.alta.crear')}
            </Boton>
          </form>
        )}
      </div>
    </div>
  );
}
